import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, isValidElement } from "react";
import { DEFAULT_IMAGE_URL, cleanProps, cn } from "../../../utils/index.js";
import { ContentfulImgOptimizer } from "../../ContentfulImgOptimizer/ContentfulImgOptimizer.js";
import { WidthCapContext } from "../../ContentfulImgOptimizer/WidthCapContext.js";
import { Heading } from "../../Heading/index.js";
import { HorizonContentfulRichText } from "../../HorizonContentfulRichText/index.js";
import { Image } from "../../Image/index.js";
import { P } from "../../Text/index.js";
import { layoutVariants } from "./ModuleLayout.variants.js";
const ModuleLayout_ModuleLayout = /*#__PURE__*/ forwardRef(function({ heading, subheading, children, contentfulRichTextSlot, image, backgroundImage, className, innerSlotClassName, contentSlotClassName, display = 'splitWithImage', imagePosition = 'topToRight', emphasis = 'regular', backgroundVariant = 'solid', panel = false, hasContentfulRichText, ...props }, ref) {
    const resolvedChildren = contentfulRichTextSlot ? /*#__PURE__*/ jsx(HorizonContentfulRichText, {
        className: "w-full",
        document: contentfulRichTextSlot,
        variant: "marketing",
        emphasis: emphasis
    }) : children;
    const resolvedHasContentfulRichText = hasContentfulRichText ?? (Boolean(contentfulRichTextSlot) || /*#__PURE__*/ isValidElement(children) && children.type === HorizonContentfulRichText);
    const { root, innerSlot, contentSlot, headerSlot, contentBodySlot, imageSlot, splitImageSlot, backgroundSlot, colorOverlaySlot } = layoutVariants({
        display,
        imagePosition,
        emphasis,
        backgroundVariant,
        panel,
        hasContentfulRichText: resolvedHasContentfulRichText
    });
    const classes = cn(root(), className);
    const domProps = cleanProps(props, Object.keys(layoutVariants.variants));
    const headingShade = 'bold' === emphasis ? '1' : '13';
    const subheadingColor = 'bold' === emphasis ? 'text-primary-3' : 'text-neutral-11';
    const renderBackgroundImage = ()=>{
        if ('image' !== backgroundVariant) return null;
        const content = backgroundImage ?? /*#__PURE__*/ jsx(Image, {
            src: DEFAULT_IMAGE_URL,
            alt: "",
            width: "full",
            height: "full",
            className: "object-cover"
        });
        return /*#__PURE__*/ jsx(WidthCapContext.Provider, {
            value: "container",
            children: /*#__PURE__*/ jsx("div", {
                className: backgroundSlot(),
                children: content
            })
        });
    };
    const renderHeading = ()=>{
        if (!heading) return null;
        const sharedProps = {
            as: 'h2',
            palette: 'neutral',
            shade: headingShade,
            textWrap: 'balance'
        };
        return /*#__PURE__*/ jsxs(Fragment, {
            children: [
                /*#__PURE__*/ jsx(Heading, {
                    ...sharedProps,
                    textStyle: "heading2",
                    className: "@2xl:hidden",
                    children: heading
                }),
                /*#__PURE__*/ jsx(Heading, {
                    ...sharedProps,
                    textStyle: "heading1",
                    className: "hidden @2xl:block",
                    children: heading
                })
            ]
        });
    };
    const renderSubheading = ()=>{
        if (!subheading) return null;
        return /*#__PURE__*/ jsxs(Fragment, {
            children: [
                /*#__PURE__*/ jsx(P, {
                    textStyle: "body1",
                    textWrap: "pretty",
                    className: cn(subheadingColor, '@2xl:hidden'),
                    children: subheading
                }),
                /*#__PURE__*/ jsx(P, {
                    textStyle: "subheading2",
                    textWrap: "pretty",
                    className: cn(subheadingColor, 'hidden @2xl:block'),
                    children: subheading
                })
            ]
        });
    };
    const renderImage = (img)=>{
        const isSplit = 'splitWithImage' === display;
        const ratio = 'withImageCenter' === display ? '21/9' : '3/2';
        const roundedClass = 'withImage' === display || 'withImageCenter' === display ? 'rounded-xl @2xl:rounded-3xl' : void 0;
        if (isSplit) return /*#__PURE__*/ jsx("div", {
            className: splitImageSlot(),
            children: 'contentfulUrl' in img ? /*#__PURE__*/ jsx(ContentfulImgOptimizer, {
                contentfulUrl: img.contentfulUrl,
                alt: img.alt,
                width: "full",
                height: "full",
                ratio: "3/2",
                className: "absolute inset-0"
            }) : /*#__PURE__*/ jsx(Image, {
                src: img.src,
                alt: img.alt,
                width: "full",
                height: "full",
                className: "absolute inset-0"
            })
        });
        return 'contentfulUrl' in img ? /*#__PURE__*/ jsx(ContentfulImgOptimizer, {
            contentfulUrl: img.contentfulUrl,
            alt: img.alt,
            ratio: ratio,
            width: "full",
            className: roundedClass
        }) : /*#__PURE__*/ jsx(Image, {
            src: img.src,
            alt: img.alt,
            ratio: ratio,
            width: "full",
            className: roundedClass
        });
    };
    const hasHeader = heading || subheading;
    const supportsImage = 'simple' !== display && 'simpleSplit' !== display && 'simpleCenter' !== display;
    const resolvedImage = supportsImage ? image : void 0;
    const sectionElement = /*#__PURE__*/ jsxs("section", {
        ref: ref,
        className: classes,
        ...domProps,
        children: [
            true !== panel && 'image' !== backgroundVariant && /*#__PURE__*/ jsx("div", {
                className: backgroundSlot(),
                "aria-hidden": "true"
            }),
            renderBackgroundImage(),
            'image' === backgroundVariant && /*#__PURE__*/ jsx("div", {
                className: colorOverlaySlot(),
                "aria-hidden": "true"
            }),
            /*#__PURE__*/ jsxs("div", {
                className: cn(innerSlot(), !resolvedImage && supportsImage && 'justify-center', innerSlotClassName),
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: cn(contentSlot(), !resolvedImage && '@2xl:w-full @2xl:max-w-none', contentSlotClassName),
                        children: [
                            hasHeader && /*#__PURE__*/ jsxs("div", {
                                className: headerSlot(),
                                children: [
                                    renderHeading(),
                                    renderSubheading()
                                ]
                            }),
                            resolvedChildren && /*#__PURE__*/ jsx("div", {
                                className: contentBodySlot(),
                                children: resolvedChildren
                            })
                        ]
                    }),
                    resolvedImage && /*#__PURE__*/ jsx("div", {
                        className: imageSlot(),
                        children: renderImage(resolvedImage)
                    })
                ]
            })
        ]
    });
    if (true === panel || 'responsive' === panel) return /*#__PURE__*/ jsx("div", {
        className: "@container w-full",
        children: sectionElement
    });
    return sectionElement;
});
const ModuleSection = ModuleLayout_ModuleLayout;
const ModuleLayout_SectionCompat = /*#__PURE__*/ forwardRef(function({ panel = true, title, description, heading, subheading, ...props }, ref) {
    return /*#__PURE__*/ jsx(ModuleLayout_ModuleLayout, {
        ref: ref,
        panel: panel,
        heading: heading ?? title,
        subheading: subheading ?? description,
        ...props
    });
});
export { ModuleLayout_ModuleLayout as ModuleLayout, ModuleLayout_SectionCompat as SectionCompat, ModuleSection };
